import * as React from "react";
import { Alert, Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from "@mui/material";

import { Suggestion, useApplySuggestion } from "@/hooks/api/use-proving-ground";

import { MONO } from "../shared";

const SEVERITY_LABEL: Record<Suggestion["severity"], string> = {
	high: "High",
	medium: "Medium",
	low: "Low",
};

/**
 * Severity in the same ink the verdicts use, so the two read as one instrument rather than
 * two colour schemes on one page.
 */
function severityInk(severity: Suggestion["severity"]) {
	return (theme: any) => {
		const key = severity === "high" ? "error" : severity === "medium" ? "warning" : "info";
		return {
			color: theme.vars.palette[key][800],
			borderColor: theme.vars.palette[key].main,
			...theme.applyStyles("dark", { color: theme.vars.palette[key][200] }),
		};
	};
}

/**
 * A proposed edit, shown the way a change to code is shown.
 *
 * Removed lines and added lines rather than prose describing them, because the question a
 * reader actually has is "what exactly would this become", and a paragraph about an edit is
 * slower to check than the edit.
 */
function Diff({ before, after }: { before: string; after: string }): React.JSX.Element | null {
	if (!before && !after) return null;

	return (
		<Box
			sx={{
				mt: 1,
				borderRadius: 1,
				border: 1,
				borderColor: "divider",
				overflow: "hidden",
				fontFamily: MONO,
				fontSize: 12.5,
				lineHeight: 1.65,
			}}
		>
			{before
				? before.split("\n").map((line, index) => (
						<Box
							key={`before-${index}`}
							sx={[
								{ px: 1.5, py: 0.25, display: "flex", gap: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" },
								// A tint carrying dark ink, not white on a pastel fill: the latter measured
								// 1.75:1 in light mode, which is unreadable, while the dark branch of the same
								// rule was already doing the right thing. Reading from `theme.vars` because in
								// cssVariables mode `theme.palette` is the light scheme resolved at build time,
								// so a semantic value read from it is pinned to one scheme.
								(theme: any) => ({
									bgcolor: `rgba(${theme.vars.palette.error.mainChannel} / 0.12)`,
									color: theme.vars.palette.error[800],
									...theme.applyStyles("dark", {
										bgcolor: `rgba(${theme.vars.palette.error.mainChannel} / 0.16)`,
										color: theme.vars.palette.error[200],
									}),
								}),
							]}
						>
							<Box component="span" sx={{ opacity: 0.7, userSelect: "none" }}>
								−
							</Box>
							<Box component="span">{line}</Box>
						</Box>
					))
				: null}
			{after
				? after.split("\n").map((line, index) => (
						<Box
							key={`after-${index}`}
							sx={[
								{ px: 1.5, py: 0.25, display: "flex", gap: 1, whiteSpace: "pre-wrap", wordBreak: "break-word" },
								(theme: any) => ({
									bgcolor: `rgba(${theme.vars.palette.success.mainChannel} / 0.12)`,
									color: theme.vars.palette.success[800],
									...theme.applyStyles("dark", {
										bgcolor: `rgba(${theme.vars.palette.success.mainChannel} / 0.16)`,
										color: theme.vars.palette.success[200],
									}),
								}),
							]}
						>
							<Box component="span" sx={{ opacity: 0.7, userSelect: "none" }}>
								+
							</Box>
							<Box component="span">{line}</Box>
						</Box>
					))
				: null}
		</Box>
	);
}

/**
 * One proposed edit, with the two things an operator needs: the argument for it, and a way to
 * change their mind about the wording before it is written.
 *
 * Editing is deliberately of the replacement text only. The text being replaced is how the
 * edit finds its place in the prompt, and letting that be retyped turns a targeted change into
 * a search that silently misses and appends to the end instead.
 */
function SuggestionCard({
	suggestion,
	index,
	runId,
	canApply,
}: {
	suggestion: Suggestion;
	index: number;
	runId: number;
	canApply: boolean;
}): React.JSX.Element {
	const apply = useApplySuggestion(runId);
	const [editing, setEditing] = React.useState(false);
	const [after, setAfter] = React.useState(suggestion.after);

	const applied = Boolean(suggestion.appliedAt);
	const changed = after !== suggestion.after;

	return (
		<Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
			<Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5, flexWrap: "wrap" }} useFlexGap>
				<Typography variant="subtitle2" sx={{ flex: "1 1 auto" }}>
					{suggestion.title}
				</Typography>
				{applied ? <Chip size="small" color="success" label="Applied" /> : null}
				<Chip
					size="small"
					variant="outlined"
					label={SEVERITY_LABEL[suggestion.severity]}
					sx={severityInk(suggestion.severity)}
				/>
				{suggestion.metrics.map((metric) => (
					<Chip key={metric} size="small" variant="outlined" label={metric} />
				))}
			</Stack>

			<Typography variant="body2" color="text.secondary">
				{suggestion.problem}
			</Typography>

			{suggestion.evidence.length ? (
				<Box sx={{ mt: 1.5 }}>
					<Typography variant="overline" color="text.secondary">
						Heard on the calls
					</Typography>
					{suggestion.evidence.map((quote, quoteIndex) => (
						<Typography
							key={quoteIndex}
							variant="body2"
							sx={{
								pl: 1.5,
								borderLeft: 2,
								borderColor: "divider",
								color: "text.secondary",
								fontStyle: "italic",
								mt: 0.5,
							}}
						>
							{quote}
						</Typography>
					))}
				</Box>
			) : null}

			{suggestion.before || after ? (
				<Box sx={{ mt: 1.5 }}>
					<Typography variant="overline" color="text.secondary">
						{suggestion.where ? `Change — ${suggestion.where}` : "Change"}
					</Typography>

					{editing ? (
						<TextField
							value={after}
							onChange={(event) => setAfter(event.target.value)}
							multiline
							minRows={4}
							fullWidth
							sx={{ mt: 1, "& .MuiInputBase-input": { fontFamily: MONO, fontSize: 12.5, lineHeight: 1.65 } }}
							helperText="This is what will be written into the prompt."
						/>
					) : (
						<Diff before={suggestion.before} after={after} />
					)}
				</Box>
			) : null}

			{canApply && (suggestion.before || after) ? (
				<Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
					{applied ? (
						<Typography variant="caption" color="text.secondary">
							Written into the prompt {new Date(suggestion.appliedAt!).toLocaleString()}.
						</Typography>
					) : (
						<React.Fragment>
							<Button
								size="small"
								variant="contained"
								disabled={apply.isPending}
								onClick={() => apply.mutate({ index, ...(changed ? { before: suggestion.before, after } : {}) })}
							>
								{apply.isPending ? "Applying…" : changed ? "Approve edited" : "Approve"}
							</Button>
							<Button size="small" color="inherit" onClick={() => setEditing(!editing)} disabled={apply.isPending}>
								{editing ? "Done editing" : "Edit"}
							</Button>
							{changed ? (
								<Button
									size="small"
									color="inherit"
									onClick={() => setAfter(suggestion.after)}
									disabled={apply.isPending}
								>
									Reset
								</Button>
							) : null}
						</React.Fragment>
					)}
				</Stack>
			) : null}
		</Box>
	);
}

export function Suggestions({
	suggestions,
	suggestionsAt,
	status,
	failedChecks,
	totalChecks,
	runId,
	canApply,
}: {
	suggestions: Suggestion[];
	suggestionsAt: string | null;
	status: string;
	failedChecks: number;
	/** Zero means nothing was scored, which is not the same as nothing failing. */
	totalChecks: number;
	runId: number;
	/** False for a manager, who sees a published run's results but never its prompt. */
	canApply: boolean;
}): React.JSX.Element | null {
	if (status !== "completed" && status !== "failed") return null;

	if (!suggestions.length) {
		// Nothing was scored at all. Distinct from a clean sweep, and the two look identical
		// unless said so: a run where every call errored has 0 of 0 checks, and reporting that
		// as "every check passed" is the most flattering possible reading of a total failure.
		if (!totalChecks) {
			return (
				<Alert severity="warning">
					{status === "failed"
						? "No calls completed, so nothing was scored. The run's error explains why."
						: "Nothing was scored — this run had no scorecard checks to apply."}
				</Alert>
			);
		}

		// Three different nothings, and telling them apart is the whole value of saying anything.
		// "Still working" especially: it looks exactly like "nothing to say" unless it says so,
		// and only one of the two means keep the page open.
		if (!suggestionsAt) {
			return failedChecks ? (
				<Alert severity="info" icon={<CircularProgress size={18} />}>
					Reading the failed calls against the prompt. This takes a minute or so on a large grid — the page fills in on
					its own.
				</Alert>
			) : (
				<Alert severity="success">Every check passed, so there is nothing to suggest.</Alert>
			);
		}

		return (
			<Alert severity={failedChecks ? "info" : "success"}>
				{failedChecks
					? "Nothing failed in a way that points at a line in the prompt. The failures here are worth reading on their own."
					: "Every check passed, so there is nothing to suggest."}
			</Alert>
		);
	}

	return (
		<Stack spacing={2}>
			<Alert severity="info">
				Read from this run&rsquo;s failures against the prompt as it stood when the run started. Approving one writes it
				into the agent&rsquo;s current prompt — edit the wording first if you want to.
			</Alert>

			{suggestions.map((suggestion, index) => (
				<SuggestionCard key={index} suggestion={suggestion} index={index} runId={runId} canApply={canApply} />
			))}
		</Stack>
	);
}
